import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useGet from "hooks/useGet";
import useCreate from "hooks/useCreate";
import { Button, Main } from "components";
import { Button as ExpanoButton } from "expano-components";

import { ReactComponent as SettingsIcon } from "icons/settings.svg";
import ChannelsList from "./components/ChannelsList";
import CampaignsList from "./components/CampaignsList";
import { compacted, getData } from "./helpers";

const Campaigns = ({
  location,
  history,
  match: {
    params: { channel_id },
  },
}) => {
  const load_more_ref = useRef(null);
  const [query_filters, setQueryFilters] = useState({
    interval: "30_days",
    show_archived: false,
  });
  const [is_loading, setLoading] = useState(false);
  const [is_fetching_next_page, setFetchingNextPage] = useState(false);

  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({
    has_next_page: false,
    past_product_group_ids: [],
    after_channel_product_id: null,
  });

  const campaign_filters = useMemo(() => {
    const { interval, show_archived, ...rest } = query_filters;
    return rest;
  }, [query_filters]);

  const interval_filter = useMemo(() => {
    return query_filters.interval;
  }, [query_filters]);

  const show_archived = useMemo(() => {
    return query_filters.show_archived;
  }, [query_filters]);

  const { data: channels = [], refetch } = useGet({
    query: ["active_campaign_channels"],
    path: "/channels",
    context: "ui_settings",
    search: {
      "q[type_eq]": "Allegro::Channel",
      per_page: 100,
    },
    error_message_enabled: false,
  });

  const { data: campaigns_today_cost = [] } = useGet({
    query: ["campaigns_today_cost", channel_id],
    path: `/channels/${channel_id}/campaigns_today_cost`,
    error_message_enabled: false,
    options: {
      enabled: !!channel_id,
    },
  });

  const { data: campaigns_stats = [] } = useGet({
    query: ["campaigns_today_cost", channel_id, interval_filter],
    path: `/channels/${channel_id}/campaigns_stats`,
    search: {
      interval: interval_filter,
    },
    error_message_enabled: false,
    options: {
      enabled: !!channel_id,
    },
  });

  useEffect(() => {
    if (channels.length > 0 && !channel_id) {
      history.push(`/campaigns/channels/${channels[0].id}`);
    }
  }, [channel_id, channels]);

  const getInitialData = useCallback(async () => {
    try {
      if (!channel_id) return;
      setLoading(true);
      const res = await getData({
        channel_id,
        query_filters: campaign_filters,
        page: 1,
      });

      setData(res.data);
      setMeta({
        ...res.meta,
        has_next_page: res.meta.next_page > 0,
      });
    } finally {
      setLoading(false);
    }
  }, [channel_id, campaign_filters]);

  const fetchNextPage = useCallback(async () => {
    try {
      setFetchingNextPage(true);
      const res = await getData({
        channel_id,
        query_filters: campaign_filters,
        page: meta.next_page,
      });
      setData([...data, ...res.data]);
      setMeta({
        ...res.meta,
        has_next_page: res.meta.next_page > 0,
      });
    } finally {
      setFetchingNextPage(false);
    }
  }, [channel_id, data, meta, campaign_filters]);

  const handleSetQueryFilters = ({ name, values }) => {
    const query = { ...query_filters };
    query[name] = values;
    setQueryFilters(compacted(query));
  };

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  useEffect(() => {
    if (!meta.has_next_page || is_fetching_next_page) return;
    if (!load_more_ref.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }, // widoczny w 100%
    );

    observer.observe(load_more_ref.current);

    return () => {
      if (load_more_ref.current) {
        // eslint-disable-next-line
        observer.unobserve(load_more_ref.current);
      }
    };
  }, [
    load_more_ref,
    meta.has_next_page,
    is_fetching_next_page,
    is_loading,
    fetchNextPage,
  ]);

  const { is_creating: is_importing, create: importCampaigns } = useCreate({
    path: `/channels/${channel_id}/channel_campaigns/import`,
    onSuccess: getInitialData,
  });

  const channel = useMemo(() => {
    if (channels.length > 0 && channel_id) {
      return channels.find(({ id }) => String(id) === String(channel_id));
    }
    return null;
  }, [channels, channel_id]);

  const setCampaignToSync = (campaign_id) =>
    setData((campaigns) =>
      campaigns.map((campaign) => ({
        ...campaign,
        sync_needed: campaign.id === campaign_id ? true : campaign.sync_needed,
      })),
    );

  const setCampaignSynced = (campaign_id) =>
    setData((campaigns) =>
      campaigns.map((campaign) => ({
        ...campaign,
        sync_needed: campaign.id === campaign_id ? false : campaign.sync_needed,
      })),
    );

  return (
    <Main
      page={{
        name: `Kampanie reklamowe ${channel?.identifier || "-"}`,
        icon: <SettingsIcon />,
        breadcrumbs: [
          { name: `Kampanie reklamowe ${channel?.identifier || "-"}` },
        ],
        buttons: channel?.allegro_ads_client_id && (
          <>
            {channel?.identifier && (
              <Button
                type="add"
                text={`Dodaj kampanię do kanału ${channel.identifier}`}
                path={`/campaigns/channels/${channel.id}/channel_campaigns/new`}
              />
            )}
            <ExpanoButton
              disabled={is_importing}
              text={is_importing ? "Proszę czekać..." : "Importuj z allegro"}
              onClick={() => importCampaigns({})}
            />
          </>
        ),
      }}
    >
      <div className="campaigns-list">
        <ChannelsList
          refetch={refetch}
          data={channels}
          channel_id={channel_id}
        />
        <div className="products-list">
          <CampaignsList
            {...{
              channel_id,
              location,
              query_filters,
              setQueryFilters,
              load_more_ref,
              handleSetQueryFilters,
              data,
              meta,
              fetchNextPage,
              is_fetching_next_page,
              campaigns_today_cost,
              campaigns_stats,
              setCampaignToSync,
              setCampaignSynced,
              show_archived,
              refetch: getInitialData,
              min_roas: channel?.min_roas || 0,
              max_roas: channel?.max_roas || 0,
              min_average_use_daily_budget: channel?.min_average_use_daily_budget || 0,
              max_average_use_daily_budget: channel?.max_average_use_daily_budget || 0,
              interval_days: interval_filter.replace('_days', '')
            }}
          />
        </div>
      </div>
    </Main>
  );
};

export default Campaigns;
